/**
 * The canvas side column's comments (RFC 0273 S1, RFC 0289 S3).
 *
 * Fills the placeholder RFC 0267 slice 5b left behind. Until now the `comment`
 * tool wrote, `capabilities.writes` gated, and the bridge enforced, while
 * nothing in the web client could READ a comment: the host stored them and
 * showed none. An app that wanted a thread beside its canvas therefore had to
 * render one inside its own view, which is how the review canvas ended up with
 * a rail competing with the diff for width.
 *
 * Scoped to the open canvas document, not the project (RFC 0273 §3), so the
 * column is always about the thing being looked at.
 *
 * WRITABLE, as of RFC 0289 S3, and that is the point of it. The column reads an
 * agent's comment and lets a person answer it in place, which wakes the session
 * that wrote it. Before this the only way to answer an agent was to leave the
 * document, find its session, and type the answer into the composer as though it
 * were a new instruction.
 *
 * GROUPED BY ANCHOR (RFC 0289 §7), not by thread. Threading links comments only
 * where somebody pressed Reply; two people who wrote about the same line
 * independently have no `parentId` between them and never will. So the block is
 * the thing being talked about, and it holds every thread written against it.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  answerComment,
  type Candidate,
  type CommentAuthor,
  type CommentEntry,
  type CommentGroup,
  type CommentThread,
  deliverMentions,
  groupThreadsByAnchor,
  type Mention,
  projectAuthor,
  projectCandidates,
  projectThreads,
  readDocumentContent,
  resolveComment,
} from '@mod/core/model'
import { useCallback, useMemo, useState } from 'react'
import { isCodeId } from '../../services/code/codeFile'
import { useCommentMap } from '../../services/comments/useCommentMap'
import { commentAuthor } from '../../services/identity/commentAuthor'
import {
  type CommentReader,
  commentReader,
  type ThreadWaiting,
  threadWaiting,
} from '../../services/sessions/commentReader'
import { useSpaceSession } from '../../services/sessions/spaceSession'
import { useDocument } from '../../services/world/repo/useDocument'
import { anchorLabel, CommentThreadView } from './CommentThreadView'

// Re-exported because the column's test imports it from here, and because a
// heading is what the column does with an anchor. The rule itself lives beside
// the thread rendering, so the editor can label a passage the same way.
export { anchorLabel }

/**
 * Unresolved first, then newest, ranked on the ROOT of each exchange.
 *
 * A resolved thread stays VISIBLE rather than being filtered out: it is history,
 * and hiding it makes an exchange look like it never happened. It simply never
 * outranks something still open.
 *
 * Ranking roots rather than comments is the whole point. The flat ordering this
 * replaces put every reply above the comment it answered, because a reply is
 * always newer than its question. Replies keep their own order underneath
 * (oldest first, from `projectThreads`), so an exchange reads downward while the
 * exchanges themselves read newest first.
 *
 * `projectThreads` already applies this rule to ONE query. This exists because
 * the column merges SEVERAL (the subject plus its `include` documents), and a
 * concatenation of sorted lists is not sorted. Pure and exported so the rule is
 * testable without React, a repo, or wasm.
 */
export function orderThreads(threads: CommentThread[]): CommentThread[] {
  return [...threads].sort((a, b) => {
    if (a.root.resolved !== b.root.resolved) return a.root.resolved ? 1 : -1
    return b.root.createdAt - a.root.createdAt
  })
}

export function CommentsSection({
  projectId,
  documentId,
  include = [],
  onGoToPassage,
}: {
  projectId: string
  /** The open canvas document. Comments are scoped to it. */
  documentId: string
  /** Scroll the document to a thread's passage (RFC 0324 3e, through RFC
   *  0328's view commands). Absent where there is no pane to move. */
  onGoToPassage?: (anchorText: string) => void
  /**
   * Extra documents whose comments belong to this surface (RFC 0273 §5): a
   * review aggregates its plan's comments so both read in one place. Declared
   * by the view over the bridge and validated by the host, so this can only
   * ever name documents in this project.
   */
  include?: string[]
}) {
  const repo = useRepo()
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const space = useSpaceSession()
  // The document's text, so a thread whose passage was deleted says so (3e).
  // Empty when it cannot be read, and then nothing is marked deleted.
  // A code file (RFC 0277 S13) has no document to read: its id is a path, and
  // asking the repo for it throws. Its threads just go unchecked.
  const [documentDoc] = useDocument(
    (isCodeId(documentId) ? undefined : documentId) as
      | AnyDocumentId
      | undefined,
  )
  const content = documentDoc ? readDocumentContent(documentDoc) : ''
  // Resolved threads hidden by default: a week of answered remarks should not
  // read as a wall of grey (RFC 0324 3e).
  const [hideResolved, setHideResolved] = useState(true)

  // projectThreads ignores undefined constraints, so passing documentId alone
  // matches comments on this document whatever else their target carries.
  // One query per document, then merged: the filter matches a SUBSET of target
  // fields, so there is no single target that means "any of these ids". Keyed
  // on a joined string rather than the array so a caller re-declaring the same
  // ids does not re-run the projection every render.
  const includeKey = include.join(',')
  // Each document's comments where they live (RFC 0339), with whatever the
  // project still holds.
  const commentMap = useCommentMap(
    projectId,
    useMemo(
      () => [documentId, ...includeKey.split(',').filter(Boolean)],
      [documentId, includeKey],
    ),
  )
  const groups = useMemo(() => {
    const ids = [documentId, ...includeKey.split(',').filter(Boolean)]
    const seen = new Set<string>()
    const merged: CommentThread[] = []
    for (const id of ids) {
      for (const t of projectThreads(commentMap, { documentId: id })) {
        // A thread could match twice if a caller included the subject; the root
        // id is the identity, so dedupe on it rather than trusting the caller.
        if (seen.has(t.root.id)) continue
        seen.add(t.root.id)
        merged.push(t)
      }
    }
    // Grouped AFTER ordering: groupThreadsByAnchor preserves the order it is
    // handed (RFC 0289 §7), so a block appears where its first thread did.
    return groupThreadsByAnchor(orderThreads(merged))
  }, [commentMap, documentId, includeKey])

  // Ranked in mod-core, so the column, the editor's popover and the review app
  // offer the same names for the same letters (RFC 0292 §3c).
  // Who wrote each comment, named in one place (RFC 0324 3a).
  const authorOf = useCallback(
    (c: CommentEntry): CommentAuthor => projectAuthor(projectDoc, c),
    [projectDoc],
  )

  // Who a draft would reach, said before it is sent (RFC 0324 3c).
  const readerFor = useCallback(
    (mentions: Mention[], replyTo?: string) =>
      commentReader({
        projectDoc,
        comments: commentMap,
        mentions,
        ...(replyTo ? { replyTo } : {}),
        space: { sessionId: space.sessionId, canStart: Boolean(space.start) },
      }),
    [projectDoc, commentMap, space],
  )

  // What each thread is waiting for, off the registry (RFC 0324 3d).
  const waitingFor = useCallback(
    (t: CommentThread) =>
      threadWaiting({
        projectDoc,
        comments: commentMap,
        thread: t,
        spaceSessionId: space.sessionId,
      }),
    [projectDoc, commentMap, space.sessionId],
  )

  const candidates = useCallback(
    (query: string): Candidate[] => projectCandidates(projectDoc, query),
    [projectDoc],
  )

  const onReply = useCallback(
    async (parentId: string, body: string, mentions: Mention[]) => {
      // answerComment, not the bare replyToComment mutation: replying to an
      // agent's comment has to WAKE its session, and this column is the main
      // place a person does that (RFC 0289).
      const author = await commentAuthor(repo, projectId)
      const out = await answerComment(repo, projectId, {
        parentId,
        author,
        body,
        ...(mentions.length > 0 ? { mentions } : {}),
      })
      // Two deliveries, not one, and they are different edges: the reply wakes
      // whoever wrote the comment being answered (RFC 0289), the mentions wake
      // whoever was named in it (RFC 0292). A thread where an agent asked a
      // question and the answer names a second session should reach both.
      if (mentions.length > 0) {
        await deliverMentions(repo, projectId, {
          commentId: out.id,
          mentions,
          author,
          body,
          documentId,
        })
      }
    },
    [repo, projectId, documentId],
  )

  const onResolve = useCallback(
    async (commentId: string, resolved: boolean) => {
      await resolveComment(repo, projectId, commentId, resolved)
    },
    [repo, projectId],
  )

  if (groups.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-muted-foreground/60 text-xs">
        No comments on this page yet.
      </div>
    )
  }

  const now = Date.now()
  const { shown, openCount, resolvedCount } = columnView(groups, hideResolved)
  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto p-1.5">
      <div className="flex items-center justify-between px-1 text-[12px] text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">{openCount}</span> open
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={hideResolved}
          onClick={() => setHideResolved(!hideResolved)}
          className="flex items-center gap-2 hover:text-foreground"
        >
          Hide resolved
          <span
            className={`relative h-4 w-7 rounded-full transition-colors ${
              hideResolved ? 'bg-blue-500' : 'bg-muted-foreground/40'
            }`}
          >
            <span
              className={`absolute top-0.5 size-3 rounded-full bg-white transition-all ${
                hideResolved ? 'left-3.5' : 'left-0.5'
              }`}
            />
          </span>
        </button>
      </div>
      {shown.length === 0 && (
        <p className="px-1 py-4 text-center text-[12px] text-muted-foreground/70">
          Nothing open. {resolvedCount} resolved.
        </p>
      )}
      {shown.map((g) => (
        <AnchorGroup
          key={g.threads[0]?.root.id ?? ''}
          group={g}
          now={now}
          onReply={onReply}
          onResolve={onResolve}
          candidates={candidates}
          authorOf={authorOf}
          readerFor={readerFor}
          waitingFor={waitingFor}
          content={content}
          onGoToPassage={onGoToPassage}
        />
      ))}
      {hideResolved && resolvedCount > 0 && shown.length > 0 && (
        <p className="px-1 pb-2 text-[12px] text-muted-foreground/70">
          {resolvedCount} resolved, hidden
        </p>
      )}
    </div>
  )
}

/** One thing being talked about, and every exchange written against it. */
function AnchorGroup({
  group,
  now,
  onReply,
  onResolve,
  candidates,
  authorOf,
  readerFor,
  waitingFor,
  content,
  onGoToPassage,
}: {
  onGoToPassage?: (anchorText: string) => void
  group: CommentGroup
  now: number
  onReply: (
    parentId: string,
    body: string,
    mentions: Mention[],
  ) => Promise<void>
  onResolve: (commentId: string, resolved: boolean) => Promise<void>
  candidates: (query: string) => Candidate[]
  authorOf: (c: CommentEntry) => CommentAuthor
  readerFor: (mentions: Mention[], replyTo?: string) => CommentReader
  waitingFor: (t: CommentThread) => ThreadWaiting | null
  /** The document's text, or '' when it could not be read. */
  content: string
}) {
  const label = anchorLabel(group.anchor)
  const anchor = group.anchor.anchorText
  // An anchor that names an OBJECT (a canvas node, an element of a page) is
  // found by its id, not by its text, so text missing from the source does not
  // mean it is gone: a page's heading reads differently in its markup, and a
  // page's script builds text the source never contains.
  const deleted = group.anchor.nodeId ? false : passageDeleted(anchor, content)
  return (
    <section className="flex flex-col gap-1.5">
      {/* What these threads are ON, when the anchor says. Without it a column of
          comments on one document is a column of remarks about nothing in
          particular, and "why does it need this?" cannot be answered. Quoted in
          grey italic, no coloured stripe (RFC 0324 design). */}
      {label && (
        <p className="flex items-baseline gap-2 px-1 text-[12px] text-muted-foreground italic">
          <PassageLabel
            label={label}
            anchor={anchor}
            deleted={deleted}
            {...(onGoToPassage ? { onGoToPassage } : {})}
          />
          {deleted && (
            <span className="shrink-0 text-[11px] text-red-500 not-italic">
              Passage deleted
            </span>
          )}
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {group.threads.map((t) => (
          <CommentThreadView
            key={t.root.id}
            thread={t}
            now={now}
            onReply={onReply}
            onResolve={onResolve}
            candidates={candidates}
            authorOf={authorOf}
            readerFor={readerFor}
            waitingFor={waitingFor}
          />
        ))}
      </ul>
    </section>
  )
}

/**
 * What the column shows (RFC 0324 3e): every group, or only open threads,
 * with the counts the header and footer need. Pure, so it is testable
 * without mounting a column.
 */
export function columnView(
  groups: CommentGroup[],
  hideResolved: boolean,
): { shown: CommentGroup[]; openCount: number; resolvedCount: number } {
  const all = groups.flatMap((g) => g.threads)
  const openCount = all.filter((t) => !t.root.resolved).length
  const shown = hideResolved
    ? groups
        .map((g) => ({
          ...g,
          threads: g.threads.filter((t) => !t.root.resolved),
        }))
        .filter((g) => g.threads.length > 0)
    : groups
  return { shown, openCount, resolvedCount: all.length - openCount }
}

/**
 * Whether a thread's passage is gone from the document. Never true when the
 * text could not be read: marking a passage deleted on a guess would tell a
 * person their comment is about nothing.
 */
export function passageDeleted(
  anchorText: string | undefined,
  content: string,
): boolean {
  if (!anchorText || !content || content.includes(anchorText)) return false
  // A page quotes the way it READS, and its source has markup between the
  // words ("details. <em>Mod brings"), so compare against the text too.
  const collapse = (s: string) => s.replace(/\s+/g, ' ').trim()
  return !collapse(content.replace(/<[^>]*>/g, '')).includes(
    collapse(anchorText),
  )
}

/**
 * The quoted passage a block of threads is about. Clicking it takes the
 * document to it (RFC 0324 3e); a deleted passage has nowhere to go, so it
 * stays text, struck through.
 */
export function PassageLabel({
  label,
  anchor,
  deleted,
  onGoToPassage,
}: {
  label: string
  anchor: string | undefined
  deleted: boolean
  onGoToPassage?: (anchorText: string) => void
}) {
  if (anchor && !deleted && onGoToPassage) {
    return (
      <button
        type="button"
        title="Go to this passage"
        onClick={() => onGoToPassage(anchor)}
        className="truncate text-left italic hover:text-foreground"
      >
        “{label}”
      </button>
    )
  }
  return (
    <span className={`truncate ${deleted ? 'line-through opacity-70' : ''}`}>
      “{label}”
    </span>
  )
}
